export const skpPipeline = [
  'Structured content',
  'AI-assisted extraction',
  'Validation rules & expert review',
  'Approved knowledge graph',
  'Semantic search & GraphRAG',
]

export const researchPriorities = [
  {
    title: 'Judgment and decision quality',
    paragraphs: [
      'We are studying how people evaluate claims, reason about consequences, handle uncertainty, respond under pressure, and revise their views when new information appears. This work informs the proposed CJI and NCJI capability models.',
    ],
  },
  {
    title: 'Measurement that can be tested',
    paragraphs: [
      'A meaningful NCJI system needs more than a score. Each measure requires defined tasks, scoring methods, uncertainty estimates, repeated-measure testing, and evidence that it works for its proposed use.',
      'The numbers and thresholds illustrated in Cognarchic State materials, including NCJI 85+, should remain conceptual until such validation exists. An ontology can organize definitions and findings; it cannot establish that a score is valid.',
    ],
  },
  {
    title: 'Development and opportunity',
    paragraphs: [
      'We are exploring how a specific finding can lead to practice, education, mentoring, or other support—and how later assessments can show whether that pathway helped. Opportunity matching should explain why a role may fit someone’s demonstrated strengths while keeping guidance separate from an eligibility decision.',
    ],
  },
  {
    title: 'Institutional learning',
    paragraphs: [
      'We are studying how organizations can define responsibilities, connect policies to decisions, detect errors, and improve their practices over time. SKP can make institutional knowledge traceable; operational systems must carry out authorized actions and preserve accountability.',
    ],
  },
]

export const civicCapabilities = [
  {
    title: 'Validated NCJI measurement',
    body: 'The assessment system would present accessible tasks, record responses, apply approved rubrics, calculate results with appropriate uncertainty, and support reassessment. A separate validation program would test whether tasks measure their stated domains and whether results are suitable for each proposed use.',
  },
  {
    title: 'Policy and decision workflows',
    body: 'A policy engine would translate approved rules into versioned procedures: what information may be considered, who may review it, which actions are permitted, and when reassessment is due. Each consequential action would produce a readable record of its grounds, policy version, and human authorization.',
  },
  {
    title: 'Identity, privacy, and access control',
    body: 'The system would verify participants and staff, record consent, restrict access by role and purpose, and log access and changes. Public knowledge would remain separate from private assessment and case records.',
  },
  {
    title: 'Appeals and restoration',
    body: 'The founding charter calls for notice, review, correction, and routes back to standing. These principles require working case queues, independent reviewers, deadlines, reassessment, and a way to correct decisions across affected systems. A person must be able to see and challenge the grounds of a consequential finding.',
  },
  {
    title: 'Institutional governance',
    body: 'People and institutions—not an LLM or graph database—must approve assessment changes, audit outcomes, review disputes, and examine whether the system treats different populations fairly. Oversight must have the authority and records needed to investigate errors and suspend a flawed rule or measure.',
  },
]

export const operationalSystems = [
  { system: 'Identity and consent', responsibility: 'Participant and staff accounts, verified roles, consent, and permission records' },
  { system: 'NCJI assessment delivery', responsibility: 'Tasks, responses, rubric versions, results, accessibility, and reassessment' },
  { system: 'Measurement and validation', responsibility: 'Reliability, uncertainty, suitability for use, and continued testing' },
  { system: 'Development pathways', responsibility: 'Practice, training, mentoring, follow-up, and outcome tracking' },
  { system: 'Opportunity and role matching', responsibility: 'Approved role requirements and explainable recommendations' },
  { system: 'Policy and decision engine', responsibility: 'Versioned rules, required approvals, notices, and decision records' },
  { system: 'Civic case management', responsibility: 'Applications, referrals, services, documents, assignments, and deadlines' },
  { system: 'Appeals and restoration', responsibility: 'Challenges, independent review, correction, and renewed assessment' },
  { system: 'Audit and oversight', responsibility: 'Access reviews, model changes, errors, appeals, and outcome monitoring' },
  { system: 'Secure integration', responsibility: 'Documented APIs and events connecting systems under permission controls' },
]

export const recordStores = [
  {
    name: 'SKP knowledge graph',
    holds: 'Capability definitions, research, rubrics, approved relationships, policy versions, and role requirements.',
  },
  {
    name: 'Protected assessment store',
    holds: 'A person’s responses and results.',
  },
  {
    name: 'Case system',
    holds: 'Applications, notices, authorized decisions, and appeals.',
  },
]

export const decisionTrail = [
  'Assessment task and response',
  'Approved scoring rule',
  'Result and uncertainty',
  'Applicable policy',
  'Human review',
  'Notice',
  'Appeal or reassessment',
  'Corrected outcome, when required',
]

export const researchAreas = [
  {
    id: 'judgment',
    title: 'Judgment and decision quality',
    lead: 'How do people evaluate claims, weigh tradeoffs, handle ambiguity, and make decisions under uncertainty?',
    paragraphs: [
      'This area examines reasoning quality, consequential thinking, foresight, emotional reactivity, and judgment under pressure. It informs the proposed capability definitions and assessment tasks for CJI and NCJI. SKP can connect those definitions and tasks to relevant research, rubrics, and later findings so they remain open to examination and revision.',
    ],
  },
  {
    id: 'manipulation',
    title: 'Manipulation resistance and information resilience',
    lead: 'People encounter propaganda, emotional framing, rhetorical deception, group pressure, and identity-driven narratives. We study when these influences weaken independent judgment and which forms of education or practice may strengthen it.',
    paragraphs: [
      'The work includes media literacy, persuasion analysis, misinformation resilience, and the study of information environments. SKP can help researchers map competing claims, trace their origins, and connect findings across disciplines. A claim that someone is “resistant” to manipulation would still require a carefully tested measure.',
    ],
  },
  {
    id: 'opportunity',
    title: 'Human opportunity and development pathways',
    lead: 'Finding a capability gap is useful only if it leads to a meaningful opportunity to grow.',
    paragraphs: [
      'We are exploring how feedback can connect people with practice, education, mentoring, community support, and roles suited to their developing strengths. This includes self-direction, execution, confidence, opportunity access, and upward mobility. Follow-up measures should show whether a pathway actually helped.',
    ],
  },
  {
    id: 'lifecycle',
    title: 'Capability across the human lifecycle',
    lead: 'The demands placed on judgment change with age, context, and responsibility. Our research considers early development, education, workforce entry, civic adulthood, institutional service, and leadership progression.',
    paragraphs: [
      'The aim is a coherent developmental model that changes with the person and the stakes involved. Early applications would emphasize learning and support. Proposed uses for high-responsibility roles would require stronger validation and more rigorous review.',
    ],
  },
  {
    id: 'institutional',
    title: 'Institutional readiness and civil service',
    lead: 'Institutions depend on more than the abilities of individual staff. Their rules, incentives, knowledge, and decision processes also shape outcomes.',
    paragraphs: [
      'This area examines workforce development, civil-service modernization, systems thinking, integrity, responsibility under pressure, and public trust. SKP can connect institutional policies and role requirements to their definitions and sources; operational systems would manage applications, services, decisions, and review.',
    ],
  },
  {
    id: 'leadership',
    title: 'Leadership readiness and governance',
    lead: 'What does sound judgment look like when a person’s decisions affect many others?',
    paragraphs: [
      'We are studying crisis reasoning, escalation control, long-term consequences, dissent management, institutional responsibility, and governance stability. The aim is to move beyond popularity or credentials as the sole signals of readiness.',
      'Any proposed leadership assessment must be tested for the specific responsibilities it is meant to inform. Its findings must be understandable, challengeable, and subject to continued reassessment.',
    ],
  },
  {
    id: 'mapping',
    title: 'Regional and national capability mapping',
    lead: 'Aggregate research may help identify opportunity bottlenecks, workforce needs, leadership development gaps, and institutional strengths across communities and regions.',
    paragraphs: [
      'This area could inform future NCJI Atlas work and strategic planning. It requires careful methods: aggregate patterns cannot be treated as judgments about every individual in a community, and sensitive personal records must remain protected.',
    ],
  },
  {
    id: 'semantic',
    title: 'Semantic knowledge and connected research',
    lead: 'SKP is also a research subject in its own right. We are exploring how governed vocabularies, expert review, knowledge graphs, semantic search, and GraphRAG can help people connect ideas across large bodies of work.',
    paragraphs: [
      'Research questions include how accurately AI proposes concepts and relationships, how reviewers resolve ambiguity, how knowledge changes over time, and how a search result can show the sources and connections behind an answer.',
      'The same infrastructure can serve scientific research and provide the approved knowledge foundation for CJI and NCJI, while keeping their domain models and private records appropriately separate.',
    ],
  },
  {
    id: 'pilots',
    title: 'Pilot programs and applied frameworks',
    lead: 'SyNeural intends to test its ideas in practical settings rather than relying on theory alone. Potential pilots include education, workforce readiness, civic capability, institutional development, and leadership training.',
    paragraphs: [
      'A pilot should begin with a defined question, an appropriate measure, a development activity, and a way to examine outcomes. Results—including failures and uncertainty—should inform revisions to both the program and its underlying knowledge model.',
    ],
  },
  {
    id: 'publications',
    title: 'White papers and publications',
    lead: 'This page will lead to SyNeural’s white papers, concept notes, research protocols, technical designs, pilot findings, and future publications.',
    paragraphs: [
      'These materials will document how our thinking develops, distinguish proposals from validated results, and give research partners a clear basis for collaboration.',
    ],
  },
]
